import { visualFromImageRef } from "@/lib/algolia/visual-from-image-ref";
import type { RichTextDescription } from "@/lib/sanity/sanity.types";
import type { ListingItem, ListingType } from "../directory-listing.types";

/**
 * Shape of the directory-relevant fields on an Algolia record. Mirrors the
 * indexer transform / `attributesToRetrieve` in `packages/algolia`. Fields
 * beyond the stores pilot are declared here so later slices (VAW-926+) can map
 * them without re-typing the hit.
 */
export interface DirectoryHit {
  objectID: string;
  type: string;
  title?: string;
  slug?: string;
  imageRef?: string;
  isLogo?: boolean;
  district?: string | null;
  building?: string | null;
  categories?: string[];
  cuisines?: string[];
  description?: string;
  markets?: string | null;
  // Extended facets retrieved for later listing types.
  startDate?: number | null;
  endDate?: number | null;
  dateLabel?: string;
  starRating?: number | null;
  hotelUrl?: string | null;
  publishedAt?: string | null;
}

/** Normalise an Algolia slug (which may omit a leading slash) into an href. */
function toHref(slug?: string): string {
  if (!slug) return "#";
  return slug.startsWith("/") ? slug : `/${slug}`;
}

/**
 * Convert an indexed Unix timestamp (seconds) back to a `YYYY-MM-DD` string,
 * the format `formatEventDateLabel` (used by the event card) expects.
 */
function unixToDate(unix?: number | null): string | null {
  if (unix == null) return null;
  return new Date(unix * 1000).toISOString().slice(0, 10);
}

/**
 * The event card renders its description as portable text, but Algolia stores
 * the resolved description as a plain string. Wrap it in a minimal single-block
 * `RichTextDescription` so the existing card renders it unchanged.
 */
function richTextFromPlain(text?: string): RichTextDescription | null {
  if (!text) return null;
  return [
    {
      _type: "block",
      _key: "algolia-description",
      style: "normal",
      markDefs: [],
      children: [{ _type: "span", _key: "0", text, marks: [] }],
    },
  ] as unknown as RichTextDescription;
}

/**
 * Convert an Algolia hit into the `ListingItem` shape consumed by
 * `config.mapToCardProps`, so the Algolia-backed directory reuses the exact
 * same card components as the GROQ path.
 *
 * Only `stores` is wired in the slice-1 pilot; subsequent listing types are
 * added alongside their migration slices.
 */
export function hitToListingItem(
  hit: DirectoryHit,
  listingType: ListingType,
): ListingItem {
  const base = {
    _id: hit.objectID,
    title: hit.title ?? null,
    visual: visualFromImageRef(hit.imageRef),
    categories: (hit.categories ?? []).map((name) => ({ _id: name, name })),
    slug: toHref(hit.slug),
    isLogo: hit.isLogo ?? null,
  };

  switch (listingType) {
    case "stores":
      return { ...base, _type: "store", district: hit.district ?? null };
    case "foodDrink":
      return {
        ...base,
        _type: "foodDrink",
        district: hit.district ?? null,
        building: hit.building ?? null,
        // Card tags pool categories + cuisines, deduped by name
        categories: [
          ...new Set([...(hit.categories ?? []), ...(hit.cuisines ?? [])]),
        ].map((name) => ({ _id: name, name })),
      };
    case "attractions":
      return { ...base, _type: "attraction", district: hit.district ?? null };
    case "articles":
      return {
        ...base,
        _type: "article",
        publishedAt: hit.publishedAt ?? null,
      };
    case "shoppingGuides":
      return {
        ...base,
        _type: "shoppingGuide",
        publishedAt: hit.publishedAt ?? null,
      };
    case "vendors":
      return { ...base, _type: "vendor", market: hit.markets ?? null };
    case "events":
      return {
        ...base,
        _type: "event",
        district: hit.district ?? null,
        startDate: unixToDate(hit.startDate),
        endDate: unixToDate(hit.endDate),
        description: richTextFromPlain(hit.description),
      };
    case "hotels":
      return {
        ...base,
        _type: "hotel",
        district: hit.district ?? null,
        // starRating is indexed as a number; the hotel card maps it back via
        // parseHotelRating (which reads the leading digit).
        rating: hit.starRating != null ? String(hit.starRating) : null,
        hotelUrl: hit.hotelUrl ?? null,
      };
    default:
      // Unreachable while ALGOLIA_DIRECTORY_TYPES is limited to the cases above.
      // Later slices add cases here as they migrate each type.
      return { ...base, _type: "store", district: hit.district ?? null };
  }
}
